// Process pipeline — Cartesian (input × logo_pos × hashtag_pos), draw
// each combo to an OffscreenCanvas, emit PNG blobs.

import { overlayHashtag } from "./overlay/chip.js";
import { overlayLogo } from "./overlay/logo.js";
import { taggedFilename } from "./overlay/naming.js";
import { decodeImage } from "./io/imageDecode.js";
import type { HashtagOptions, InputFile, LogoOptions, ResultItem } from "./state.js";
import { THEME_COLORS, type Variant } from "./overlay/constants.generated.js";
import { resolveLogoVariant } from "./overlay/background.js";
import type { Position } from "./overlay/positions.js";

export interface ProcessProgressCallback {
  (current: number, total: number): void;
}

export interface ProcessEvent {
  type: "result" | "error";
  result?: ResultItem;
  error?: { inputName: string; message: string };
}

export interface ProcessCallbacks {
  onProgress: ProcessProgressCallback;
  onResult: (r: ResultItem) => void;
  onError: (err: { inputName: string; message: string }) => void;
}

/**
 * Make sure GenSenRounded font is loaded before any chip render uses
 * it. Otherwise Canvas falls back to the next font-family which causes
 * the first batch's chips to look different from later ones.
 */
export async function ensureChipFontsReady(): Promise<void> {
  // Ask the browser to load the bold weight we'll use; idempotent.
  await Promise.all([
    document.fonts.load(`700 32px "GenSenRounded W7"`),
    document.fonts.load(`700 32px "Noto Sans TC Bold"`),
  ]).catch(() => {
    // Network-load failure is non-fatal — fallback fonts in the CSS stack will be used.
  });
}

export async function processBatch(
  inputs: InputFile[],
  logoOpts: LogoOptions,
  hashtagOpts: HashtagOptions,
  signal: AbortSignal,
  cb: ProcessCallbacks,
): Promise<void> {
  await ensureChipFontsReady();

  // Pre-compute the per-image work item count for progress.
  const perImage = logoOpts.positions.length * hashtagOpts.positions.length;
  const total = inputs.length * perImage;
  let done = 0;

  const chipFill =
    hashtagOpts.fillMode === "custom"
      ? hashtagOpts.customColor
      : THEME_COLORS[hashtagOpts.theme]!;

  for (const input of inputs) {
    if (signal.aborted) return;

    let decoded;
    try {
      decoded = await decodeImage(input.file);
    } catch (err) {
      cb.onError({ inputName: input.file.name, message: (err as Error).message });
      done += perImage; // count this input's quota even though it failed
      cb.onProgress(done, total);
      continue;
    }

    const resolvedVariant: Variant = await resolveLogoVariant(
      decoded.bitmap,
      logoOpts.variant,
    );

    for (const logoPos of logoOpts.positions) {
      for (const chipPos of hashtagOpts.positions) {
        if (signal.aborted) {
          decoded.bitmap.close();
          return;
        }
        try {
          const blob = await renderOne(
            decoded.bitmap,
            decoded.width,
            decoded.height,
            logoOpts.layout,
            resolvedVariant,
            logoPos,
            hashtagOpts.text,
            chipPos,
            chipFill,
            hashtagOpts.textColor,
            hashtagOpts.chipStyle,
            hashtagOpts.fontKey,
          );
          const filename = taggedFilename(input.file.name, "overlay");
          cb.onResult({
            id: cryptoRandomId(),
            filename,
            blob,
            thumbUrl: URL.createObjectURL(blob),
            inputId: input.id,
            selected: true,
          });
        } catch (err) {
          cb.onError({ inputName: input.file.name, message: (err as Error).message });
        }
        done++;
        cb.onProgress(done, total);
      }
    }
    decoded.bitmap.close();
  }
}

async function renderOne(
  source: ImageBitmap,
  w: number,
  h: number,
  layout: string,
  variant: Variant,
  logoPos: Position,
  hashtagText: string,
  chipPos: Position,
  fillHex: string,
  textHex: string,
  chipStyle: string,
  fontKey: import("./overlay/chip.js").ChipFontKey,
): Promise<Blob> {
  const canvas = new OffscreenCanvas(w, h);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("could not acquire 2d context for compose");
  ctx.drawImage(source, 0, 0);

  await overlayLogo(canvas, ctx, {
    layout: layout as import("./overlay/constants.generated.js").Layout,
    variant,
    position: logoPos,
  });
  overlayHashtag(canvas, ctx, {
    text: hashtagText,
    position: chipPos,
    fillHex,
    textHex,
    style: chipStyle as import("./overlay/constants.generated.js").ChipStyle,
    fontKey,
  });
  return canvas.convertToBlob({ type: "image/png" });
}

function cryptoRandomId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return String(Math.random()).slice(2);
}
